import { useCallback, useState } from 'react';
import { Popover } from 'antd';
import clsx from 'clsx';
import { PiCaretDownFill, PiCaretUpFill } from 'react-icons/pi';
import { useAuth } from '../../../../../context/AuthContext';
import { useAssetOverview } from '../../../../../context/AssetOverviewContext';
import { useCloseOnMapPointerDown } from '../../../../../hooks/useCloseOnMapPointerDown';
import { AssetSortOrder } from '../../../../../enums/assetSortOrder';
import { AllowedRoles } from '../../../../../routes/routeConstants/allowedRoles';
import ConditionalRender from '../../../../../shared/components/ConditionalRender';
import EditAssetListModal from '../EditAssetListModal';
import styles from './AssetListPanel.module.scss';

interface AssetListHeaderProps {
  sortOrder: AssetSortOrder | null;
  onToggleSort: () => void;
}

const AssetListHeader = ({ sortOrder, onToggleSort }: AssetListHeaderProps) => {
  const { canAccess } = useAuth();
  const canEditAssetList = canAccess({ allowedRoles: AllowedRoles.EDITOR_AND_ABOVE });
  const { config } = useAssetOverview();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const nextSortLabel = sortOrder === AssetSortOrder.ASCENDING ? 'descending' : 'ascending';

  const handleEditClose = useCallback(() => setIsEditOpen(false), []);

  useCloseOnMapPointerDown(isEditOpen, handleEditClose);

  return (
    <header className={styles.assetListHeader}>
      <h2 className={styles.assetListTitle}>Assets</h2>
      {/* Stacked carets mirroring antd's table sorter: each lights up for its own direction. */}
      <button type="button" className={styles.assetListSortButton} onClick={onToggleSort} aria-label={`Sort assets ${nextSortLabel}`}>
        <PiCaretUpFill className={clsx(styles.assetListSortCaret, sortOrder === AssetSortOrder.ASCENDING && styles.assetListSortCaretActive)} />
        <PiCaretDownFill className={clsx(styles.assetListSortCaret, sortOrder === AssetSortOrder.DESCENDING && styles.assetListSortCaretActive)} />
      </button>

      <ConditionalRender visible={canEditAssetList}>
        <Popover
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          trigger="click"
          placement="rightTop"
          arrow={false}
          destroyTooltipOnHide
          content={config ? <EditAssetListModal config={config} onClose={handleEditClose} /> : null}
        >
          <button type="button" className={styles.assetListEditButton} aria-label="Edit asset list" disabled={!config}>
            <img src="/assets/pencil-icon-light.svg" alt="" />
          </button>
        </Popover>
      </ConditionalRender>
    </header>
  );
};

export default AssetListHeader;
